import classNames from 'classnames';
import {Typography} from 'components/Typography';
import style from './style.module.scss';
import {ILabel, IProductShort} from 'modules/products/types';
import React, {memo, useCallback} from 'react';
import {Tooltip} from 'components/Tooltip';
import Icon from 'components/Icon';
import {useIsMobileByScreen} from 'core/hooks/useIsMobileByScreen';
import {IAnalyticsMeta} from 'core/types';
import {useProductLabelClickAnalytics} from '../../hooks/gtag/useProductLabelClickAnalytics';
import Link from 'components/CustomRoutingLink';
import {useGrowingDiscountGtag} from 'modules/product-card/analytics/gtag/hooks/useGrowingDiscountGtag';

interface IProps {
    labels: ILabel[];
    isShortForm?: boolean;
    isOutOfStock?: boolean;
    id: IProductShort['id'];
    analytics?: IAnalyticsMeta;
}

export const LABELS_THEME = {
    CUSTOMER_CHOICE: 'customer',
    GIFT_IN_CART: 'gift',
    GROWING_DISCOUNT: 'discount',
    IN_STOCK: 'stock',
    IS_NEW: 'new',
    EXPERTS_CHOICE: 'expert',
    ONLINE_ONLY: 'online_only',
};

const LABELS_CODES = {
    CUSTOMER_CHOICE: 'CUSTOMER_CHOICE',
    GIFT_IN_CART: 'GIFT_IN_CART',
    GROWING_DISCOUNT: 'GROWING_DISCOUNT',
    IN_STOCK: 'IN_STOCK',
    IS_NEW: 'IS_NEW',
    EXPERTS_CHOICE: 'EXPERTS_CHOICE',
    ONLINE_ONLY: 'ONLINE_ONLY',
};

type TLabelsCode = keyof typeof LABELS_CODES;

export const ProductLabels = memo(
    ({labels, isShortForm, id, analytics, isOutOfStock = false}: IProps) => {
        const productShieldClickGtag = useProductLabelClickAnalytics();
        const {handleShieldClick: handleGrowingDiscountShieldClick} = useGrowingDiscountGtag();

        const labelsList = isShortForm ? labels?.slice(0, 2) : labels;
        const isMobile = useIsMobileByScreen();

        const renderOverlay = useCallback((saleTitle: string) => {
            return (
                <div
                    className={style.tooltipContent}
                    style={{
                        zIndex: 100,
                    }}
                >
                    <Typography variant={'label-12-r'}>Участвует в акции «{saleTitle}»</Typography>
                </div>
            );
        }, []);

        return (
            <>
                {labelsList?.map(({title: labelTitle, code, saleTitle, url, shortDescription}) => {
                    if (code === LABELS_CODES.IN_STOCK || code === LABELS_CODES.GIFT_IN_CART) {
                        if (isOutOfStock) {
                            return null;
                        }
                        return (
                            <div data-skeleton-item={true} key={code} className={style.labelWithTooltip}>
                                <Tooltip
                                    childrenClassName={style.labelTooltip}
                                    id={id?.toString() + `/sale${saleTitle}`}
                                    overlay={renderOverlay(saleTitle)}
                                >
                                    {/* TODO: ВНИМАНИЕ! Не использовать Link, т.к. не все страницы еще готовы на новом фронте */}
                                    <Link
                                        className={style.labelLink}
                                        href={url}
                                        onClick={productShieldClickGtag(id, analytics, url)}
                                    >
                                        <Typography
                                            className={classNames(
                                                style.label,
                                                style[LABELS_THEME[code as TLabelsCode]],
                                            )}
                                        >
                                            {labelTitle}
                                        </Typography>
                                    </Link>
                                </Tooltip>
                            </div>
                        );
                    }

                    if (code === LABELS_CODES.EXPERTS_CHOICE) {
                        return (
                            <div data-skeleton-item={true} key={code} className={style.labelWithTooltip}>
                                <Tooltip
                                    childrenClassName={style.labelTooltip}
                                    id={id?.toString() + '/expert'}
                                    hidden={isMobile}
                                    overlay={
                                        <div className={style.tooltipContent}>
                                            <Typography variant={'label-12-r'}>
                                                Наши эксперты отобрали наиболее популярные и редкие товары — от трендов
                                                до вечной классики — доступные только в SimpleWine.
                                            </Typography>
                                        </div>
                                    }
                                >
                                    <div
                                        className={classNames(
                                            style.label,
                                            style.labelWithIcon,
                                            style[LABELS_THEME[code as TLabelsCode]],
                                        )}
                                    >
                                        <Icon className={style.labelIcon} icon={'shine-filled'} />
                                        <span>{labelTitle}</span>
                                    </div>
                                </Tooltip>
                            </div>
                        );
                    }

                    if (code === LABELS_CODES.ONLINE_ONLY) {
                        return null;
                    }

                    if (code === LABELS_CODES.GROWING_DISCOUNT) {
                        if (isOutOfStock) {
                            return null;
                        }
                        return (
                            <div data-skeleton-item={true} key={code} className={style.labelWithTooltip}>
                                <Tooltip
                                    childrenClassName={style.labelTooltip}
                                    id={id?.toString() + '/growing_discount'}
                                    hidden={isMobile}
                                    overlay={
                                        <div className={style.tooltipContent}>
                                            <Typography variant={'label-12-r'}>{shortDescription}</Typography>
                                        </div>
                                    }
                                >
                                    <Link
                                        className={style.labelLink}
                                        href={url}
                                        onClick={handleGrowingDiscountShieldClick}
                                    >
                                        <div
                                            className={classNames(
                                                style.label,
                                                style[LABELS_THEME[code as TLabelsCode]],
                                            )}
                                        >
                                            <span>{labelTitle}</span>
                                        </div>
                                    </Link>
                                </Tooltip>
                            </div>
                        );
                    }

                    return (
                        // ВНИМАНИЕ! Не использовать Link, т.к. не все страницы еще готовы на новом фронте
                        <a
                            data-skeleton-item={true}
                            className={style.labelLink}
                            href={url}
                            key={code}
                            onClick={productShieldClickGtag(id, analytics, url)}
                        >
                            <span className={classNames(style.label, style[LABELS_THEME[code as TLabelsCode]])}>
                                {labelTitle}
                            </span>
                        </a>
                    );
                })}
            </>
        );
    },
    (prev, next) =>
        prev.isShortForm === next.isShortForm &&
        prev.labels.length === next.labels.length &&
        prev.labels.every(
            (label, index) => label.code === next.labels[index].code && label.title === next.labels[index].title,
        ),
);
